import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { format, getISODay, parseISO } from 'date-fns';
import { supabase } from '../lib/supabase';
import { errorMessage, formatTime, fullName } from '../lib/format';
import { deleteLiabilityForm, liabilityFormUrl, uploadLiabilityForm } from '../lib/liability';
import { getLiabilityTemplate, templateAttachment, type LiabilityTemplate } from '../lib/documents';
import type { Client, LiabilityStatus } from '../lib/types';
import { PageHead } from '../components/AppLayout';
import { Modal } from '../components/Modal';
import { ClassPicker } from '../components/ClassPicker';
import { EmailComposeModal } from '../components/EmailComposeModal';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';

const WAIVER_MESSAGE = `Thanks for signing up with Top Dogs Training Academy! Before your dog's first class, please review, sign, and return the attached liability waiver.

You can reply to this email with a photo or scan of the signed form, or bring it along to your first session.

See you and your pup soon!`;

const LIABILITY_LABEL: Record<LiabilityStatus, { text: string; cls: string }> = {
  none: { text: 'Not sent', cls: 'badge-neutral' },
  sent: { text: 'Sent', cls: 'badge-warn' },
  signed: { text: 'Signed', cls: 'badge-success' },
};

interface ScheduleEntry {
  classId: string;
  when: string;
}

export function ClientsPage() {
  const [rows, setRows] = useState<Client[]>([]);
  const [schedules, setSchedules] = useState<Map<string, ScheduleEntry[]>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();
  const confirm = useConfirm();
  const [editing, setEditing] = useState<Client | null>(null);
  const [viewing, setViewing] = useState<Client | null>(null);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState<'active' | 'inactive' | 'past'>('active');
  const [emailing, setEmailing] = useState<Client | null>(null);
  const [classModal, setClassModal] = useState<Client | null>(null);
  const [template, setTemplate] = useState<LiabilityTemplate | null>(null);
  // Only the very first load shows the skeleton — a reload after an
  // action (booking, status change) just swaps the data in place.
  const loadedOnce = useRef(false);

  const reload = useCallback(async () => {
    if (!loadedOnce.current) setLoading(true);
    const [c, e] = await Promise.all([
      supabase.from('clients').select('*').order('first_name'),
      supabase
        .from('enrollments')
        .select('client_id, classes(id, start_at, status)')
        .order('client_id'),
    ]);
    const firstErr = c.error || e.error;
    if (firstErr) setError(errorMessage(firstErr));
    else setError(null);
    setRows((c.data ?? []) as Client[]);

    const map = new Map<string, ScheduleEntry[]>();
    type EnrollmentRow = {
      client_id: string;
      classes: { id: string; start_at: string; status: string } | null;
    };
    for (const row of (e.data ?? []) as unknown as EnrollmentRow[]) {
      if (!row.classes || row.classes.status === 'cancelled') continue;
      const when = `${format(parseISO(row.classes.start_at), 'EEE')} · ${formatTime(row.classes.start_at)}`;
      const existing = map.get(row.client_id) ?? [];
      if (!existing.some((entry) => entry.classId === row.classes!.id)) {
        map.set(row.client_id, [...existing, { classId: row.classes!.id, when }]);
      }
    }
    setSchedules(map);
    setLoading(false);
    loadedOnce.current = true;
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    getLiabilityTemplate()
      .then(setTemplate)
      .catch(() => setTemplate(null));
  }, []);

  async function setActive(row: Client, active: boolean) {
    const { error } = await supabase.from('clients').update({ active }).eq('id', row.id);
    if (error) toast.error(errorMessage(error));
    else {
      toast.success(active ? `${fullName(row)} marked active.` : `${fullName(row)} marked past.`);
      void reload();
    }
  }

  // Pull a booked client off their current class(es) without losing them —
  // they stay a live client, just one that needs booking again. Returns
  // whether it actually happened, so callers (like the view modal) know
  // whether to close.
  async function setInactive(row: Client): Promise<boolean> {
    const ok = await confirm({
      title: `Mark ${fullName(row)} as needing booking?`,
      body: "They'll be taken off any class rosters they're currently on, but stay listed as a current client.",
      confirmLabel: 'Set inactive',
    });
    if (!ok) return false;
    const { error } = await supabase.from('enrollments').delete().eq('client_id', row.id);
    if (error) {
      toast.error(errorMessage(error));
      return false;
    }
    toast.success(`${fullName(row)} taken off their class(es) — now needs booking.`);
    void reload();
    return true;
  }

  async function setPastClient(row: Client): Promise<boolean> {
    const ok = await confirm({
      title: `Mark ${fullName(row)} as a past client?`,
      body: "They'll be taken off any class rosters and moved to Past clients. You can reactivate them later.",
      confirmLabel: 'Set past client',
      danger: true,
    });
    if (!ok) return false;
    const { error: unenrollError } = await supabase
      .from('enrollments')
      .delete()
      .eq('client_id', row.id);
    if (unenrollError) {
      toast.error(errorMessage(unenrollError));
      return false;
    }
    const { error } = await supabase.from('clients').update({ active: false }).eq('id', row.id);
    if (error) {
      toast.error(errorMessage(error));
      return false;
    }
    toast.success(`${fullName(row)} marked as a past client.`);
    void reload();
    return true;
  }

  // After emailing the blank waiver, bump a brand-new client to "Sent"
  // (don't touch someone already marked Sent or Signed).
  async function markWaiverSent(client: Client) {
    if (client.liability_status === 'none') {
      const { error } = await supabase
        .from('clients')
        .update({ liability_status: 'sent' })
        .eq('id', client.id);
      if (error) toast.error(errorMessage(error));
    }
    void reload();
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const byTab = rows.filter((c) => {
      if (!c.active) return tab === 'past';
      const booked = (schedules.get(c.id)?.length ?? 0) > 0;
      return tab === 'active' ? booked : tab === 'inactive' ? !booked : false;
    });
    if (!q) return byTab;
    return byTab.filter((c) =>
      [c.first_name, c.last_name, c.dog_name, c.email, c.phone, c.breed]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [rows, search, tab, schedules]);

  async function remove(row: Client) {
    const ok = await confirm({
      title: `Delete ${fullName(row)}?`,
      body: `Removes ${row.dog_name ? `${row.dog_name} and ` : ''}their record, and takes them off any class rosters. This can't be undone.`,
      confirmLabel: 'Delete client',
      danger: true,
    });
    if (!ok) return;
    // Clean up their stored liability form too, if any.
    if (row.liability_form_path) {
      try {
        await deleteLiabilityForm(row.liability_form_path);
      } catch {
        // Best effort — the row delete is what matters.
      }
    }
    const { error } = await supabase.from('clients').delete().eq('id', row.id);
    if (error) toast.error(errorMessage(error));
    else {
      toast.success(`${fullName(row)} deleted.`);
      void reload();
    }
  }

  async function viewForm(row: Client) {
    if (!row.liability_form_path) return;
    // Open the tab first so the browser treats it as user-initiated.
    const w = window.open('', '_blank');
    try {
      const url = await liabilityFormUrl(row.liability_form_path);
      if (w) w.location.href = url;
    } catch (e) {
      w?.close();
      toast.error(errorMessage(e));
    }
  }

  return (
    <>
      <PageHead
        title="Clients"
        subtitle="Your clients and their dogs."
        action={
          <button className="btn btn-primary" onClick={() => setCreating(true)}>
            + Add client
          </button>
        }
      />

      <div className="card">
        <div
          className="row wrap"
          style={{ marginBottom: '0.9rem', justifyContent: 'space-between' }}
        >
          <div className="row wrap" style={{ gap: '0.4rem' }}>
            <button
              className={`btn btn-sm ${tab === 'active' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setTab('active')}
            >
              Active clients
            </button>
            <button
              className={`btn btn-sm ${tab === 'inactive' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setTab('inactive')}
            >
              Inactive clients
            </button>
            <button
              className={`btn btn-sm ${tab === 'past' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setTab('past')}
            >
              Past clients
            </button>
          </div>
          <input
            placeholder="Search by name, dog, breed, email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 340 }}
          />
        </div>
        {error && <p className="error-text">{error}</p>}
        {loading ? (
          <div style={{ padding: '0.4rem 0' }}>
            <span className="skel" style={{ width: '68%' }} />
            <span className="skel" style={{ width: '52%' }} />
            <span className="skel" style={{ width: '60%' }} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="empty">
            <span className="empty-emoji">🐕</span>
            {rows.length === 0
              ? 'No clients yet. Add your first one.'
              : tab === 'active'
                ? 'No active clients match that search.'
                : tab === 'inactive'
                  ? 'No one needs booking right now.'
                  : 'No past clients match that search.'}
          </div>
        ) : (
          <table className="table cards">
            <thead>
              <tr>
                <th>Client</th>
                <th>Dog</th>
                <th>Contact</th>
                {tab === 'active' && <th>Day / time</th>}
                <th>Liability</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr key={row.id}>
                  <td style={{ fontWeight: 600 }}>{fullName(row)}</td>
                  <td data-label="Dog">
                    <span>
                      {row.dog_name || '—'}
                      {row.breed && <span className="muted small"> · {row.breed}</span>}
                      {row.age != null && <span className="muted small"> · {row.age}y</span>}
                    </span>
                  </td>
                  <td className="muted small" data-label="Contact">
                    <span>
                      {row.email || '—'}
                      {row.phone && <div>{row.phone}</div>}
                    </span>
                  </td>
                  {tab === 'active' && (
                    <td className="muted small" data-label="Day / time">
                      {(schedules.get(row.id) ?? []).length > 0
                        ? schedules
                            .get(row.id)!
                            .map((s) => s.when)
                            .join(', ')
                        : '—'}
                    </td>
                  )}
                  <td data-label="Waiver">
                    <span className={`badge ${LIABILITY_LABEL[row.liability_status].cls}`}>
                      {LIABILITY_LABEL[row.liability_status].text}
                    </span>
                    {row.liability_form_path && (
                      <>
                        {' '}
                        <button
                          className="linkish small"
                          onClick={() => void viewForm(row)}
                          title="Open the signed form"
                        >
                          View
                        </button>
                      </>
                    )}
                  </td>
                  <td>
                    <div className="row wrap" style={{ justifyContent: 'flex-end' }}>
                      {tab === 'active' && (
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => setClassModal(row)}
                        >
                          Modify Classes
                        </button>
                      )}
                      {tab === 'inactive' && (
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => setClassModal(row)}
                        >
                          + Add to class
                        </button>
                      )}
                      <button
                        className="btn btn-ghost btn-sm"
                        disabled={!template || !row.email}
                        title={
                          !template
                            ? 'Upload a blank waiver in Settings first'
                            : !row.email
                              ? 'This client has no email on file'
                              : 'Email the blank waiver to sign'
                        }
                        onClick={() => setEmailing(row)}
                      >
                        Email waiver
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={() => setViewing(row)}>
                        View
                      </button>
                      {tab === 'past' && (
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => void setActive(row, true)}
                        >
                          Set active
                        </button>
                      )}
                      {tab === 'active' && (
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => void setInactive(row)}
                        >
                          Set Inactive
                        </button>
                      )}
                      {tab === 'inactive' && (
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => void setPastClient(row)}
                        >
                          Set past client
                        </button>
                      )}
                      {tab === 'past' && (
                        <button className="btn btn-ghost btn-sm" onClick={() => void remove(row)}>
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {(creating || editing) && (
        <ClientForm
          client={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            void reload();
          }}
        />
      )}

      {emailing && template && (
        <EmailComposeModal
          title={`Email waiver to ${fullName(emailing)}`}
          recipients={emailing.email ? [{ email: emailing.email, name: emailing.first_name }] : []}
          defaultSubject="Your liability waiver — Top Dogs Training Academy"
          defaultMessage={WAIVER_MESSAGE}
          presetAttachments={[templateAttachment(template)]}
          presetAttachmentLabel={template.filename}
          sendLabel="Send waiver"
          onClose={() => setEmailing(null)}
          onSent={() => void markWaiverSent(emailing)}
        />
      )}

      {classModal && (
        <ManageClassModal
          client={classModal}
          onClose={() => setClassModal(null)}
          onChanged={() => void reload()}
        />
      )}

      {viewing && (
        <ClientViewModal
          client={viewing}
          tab={tab}
          onClose={() => setViewing(null)}
          onEdit={() => {
            setEditing(viewing);
            setViewing(null);
          }}
          onSetInactive={async () => {
            if (await setInactive(viewing)) setViewing(null);
          }}
          onSetPastClient={async () => {
            if (await setPastClient(viewing)) setViewing(null);
          }}
        />
      )}
    </>
  );
}

function ClientViewModal({
  client,
  tab,
  onClose,
  onEdit,
  onSetInactive,
  onSetPastClient,
}: {
  client: Client;
  tab: 'active' | 'inactive' | 'past';
  onClose: () => void;
  onEdit: () => void;
  onSetInactive: () => void;
  onSetPastClient: () => void;
}) {
  const detail = (label: string, value: string | null) =>
    value ? (
      <div className="iq-detail-row">
        <span className="iq-detail-label">{label}</span>
        <span>{value}</span>
      </div>
    ) : null;

  return (
    <Modal
      title={fullName(client)}
      onClose={onClose}
      footer={
        <>
          {tab === 'active' && (
            <button className="btn btn-ghost btn-sm" onClick={() => void onSetInactive()}>
              Set inactive
            </button>
          )}
          {tab !== 'past' && (
            <button className="btn btn-ghost btn-sm" onClick={() => void onSetPastClient()}>
              Set past client
            </button>
          )}
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
          <button className="btn btn-primary" onClick={onEdit}>
            Edit
          </button>
        </>
      }
    >
      <div className="iq-detail">
        {detail('Dog name', client.dog_name)}
        {detail('Breed', client.breed)}
        {detail('Dog age', client.age != null ? `${client.age} years` : null)}
        {detail('Phone', client.phone)}
        {detail('Email', client.email)}
        <div className="iq-detail-row">
          <span className="iq-detail-label">Liability</span>
          <span className={`badge ${LIABILITY_LABEL[client.liability_status].cls}`}>
            {LIABILITY_LABEL[client.liability_status].text}
          </span>
        </div>
        {client.notes && (
          <div className="iq-detail-row iq-detail-block">
            <span className="iq-detail-label">Notes</span>
            <p style={{ margin: '0.3rem 0 0' }}>{client.notes}</p>
          </div>
        )}
      </div>
    </Modal>
  );
}

interface EnrolledClass {
  id: string;
  title: string | null;
  start_at: string;
  class_types: { name: string } | null;
  instructors: { name: string } | null;
}

function classLabel(c: EnrolledClass): string {
  return c.title || c.class_types?.name || 'Class';
}

// Lists everything the client is currently enrolled in (each with its
// own Remove) plus a picker to add another class. "Changing" a class is
// just removing the old one and adding the new one here — no separate
// bulk-swap step. Clearing their last class simply leaves them with no
// bookings, so they naturally show up under Inactive next time the list
// reloads.
function ManageClassModal({
  client,
  onClose,
  onChanged,
}: {
  client: Client;
  onClose: () => void;
  onChanged: () => void;
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const [current, setCurrent] = useState<EnrolledClass[] | null>(null);
  const [classId, setClassId] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const loadCurrent = useCallback(async () => {
    const { data } = await supabase
      .from('enrollments')
      .select('classes(id, title, start_at, status, class_types(name), instructors(name))')
      .eq('client_id', client.id);
    type Row = { classes: (EnrolledClass & { status: string }) | null };
    const rows = ((data ?? []) as unknown as Row[])
      .map((r) => r.classes)
      .filter((c): c is EnrolledClass & { status: string } => Boolean(c) && c!.status === 'active')
      .sort((a, b) => {
        const da = parseISO(a.start_at);
        const db = parseISO(b.start_at);
        const dayCmp = getISODay(da) - getISODay(db);
        if (dayCmp !== 0) return dayCmp;
        return format(da, 'HH:mm').localeCompare(format(db, 'HH:mm'));
      });
    setCurrent(rows);
  }, [client.id]);

  useEffect(() => {
    void loadCurrent();
  }, [loadCurrent]);

  const excludeClassIds = useMemo(() => new Set((current ?? []).map((c) => c.id)), [current]);

  async function removeOne(cls: EnrolledClass) {
    const ok = await confirm({
      title: `Remove from ${classLabel(cls)}?`,
      body: `${fullName(client)} will be taken off this class's roster.`,
      confirmLabel: 'Remove',
      danger: true,
    });
    if (!ok) return;
    const { error } = await supabase
      .from('enrollments')
      .delete()
      .eq('class_id', cls.id)
      .eq('client_id', client.id);
    if (error) {
      toast.error(errorMessage(error));
      return;
    }
    toast.success(`Removed from ${classLabel(cls)}.`);
    void loadCurrent();
    onChanged();
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!classId) return;
    setBusy(true);
    setErr(null);
    const { error } = await supabase
      .from('enrollments')
      .insert({ class_id: classId, client_id: client.id });
    setBusy(false);
    if (error) setErr(errorMessage(error));
    else {
      toast.success(`${fullName(client)} added to the class.`);
      onChanged();
      onClose();
    }
  }

  return (
    <Modal title={`Modify classes — ${fullName(client)}`} onClose={onClose}>
      <div className="field">
        <label>Currently enrolled</label>
        {current === null ? (
          <p className="muted small" style={{ margin: 0 }}>
            Loading…
          </p>
        ) : current.length === 0 ? (
          <p className="muted small" style={{ margin: 0 }}>
            Not currently enrolled in any class.
          </p>
        ) : (
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {current.map((c) => (
              <li key={c.id} className="row between" style={{ padding: '0.3rem 0', gap: '0.5rem' }}>
                <span className="small">
                  {format(parseISO(c.start_at), 'EEE')} · {formatTime(c.start_at)} — {classLabel(c)}{' '}
                  ({c.instructors?.name ?? '—'})
                </span>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => void removeOne(c)}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <form onSubmit={submit}>
        <ClassPicker value={classId} onChange={setClassId} excludeClassIds={excludeClassIds} />
        {err && <p className="error-text">{err}</p>}
        <div className="row" style={{ justifyContent: 'flex-end', marginTop: '0.6rem' }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy || !classId}>
            {busy ? 'Adding…' : 'Add class'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ClientForm({
  client,
  onClose,
  onSaved,
}: {
  client: Client | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const fileInput = useRef<HTMLInputElement>(null);
  const [f, setF] = useState({
    first_name: client?.first_name ?? '',
    last_name: client?.last_name ?? '',
    phone: client?.phone ?? '',
    email: client?.email ?? '',
    dog_name: client?.dog_name ?? '',
    breed: client?.breed ?? '',
    age: client?.age != null ? String(client.age) : '',
    notes: client?.notes ?? '',
    liability_status: client?.liability_status ?? ('none' as LiabilityStatus),
  });
  // The stored form file is managed live (uploads happen immediately),
  // separate from the Save button.
  const [formPath, setFormPath] = useState(client?.liability_form_path ?? null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: keyof typeof f, v: string) => setF((prev) => ({ ...prev, [k]: v }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    const payload = {
      first_name: f.first_name.trim(),
      last_name: f.last_name.trim(),
      phone: f.phone.trim() || null,
      email: f.email.trim() || null,
      dog_name: f.dog_name.trim() || null,
      breed: f.breed.trim() || null,
      age: f.age ? Number(f.age) : null,
      notes: f.notes.trim() || null,
      liability_status: f.liability_status,
    };
    const query = client
      ? supabase.from('clients').update(payload).eq('id', client.id)
      : supabase.from('clients').insert(payload);
    const { error } = await query;
    setBusy(false);
    if (error) setErr(errorMessage(error));
    else {
      toast.success(client ? 'Client updated.' : 'Client added.');
      onSaved();
    }
  }

  async function onFilePicked(file: File | null) {
    if (!file || !client) return;
    setUploading(true);
    setErr(null);
    try {
      const newPath = await uploadLiabilityForm(client.id, file);
      const { error } = await supabase
        .from('clients')
        .update({ liability_form_path: newPath, liability_status: 'signed' })
        .eq('id', client.id);
      if (error) throw error;
      // Replacing? Clean up the old file after the new one is in place.
      if (formPath && formPath !== newPath) {
        try {
          await deleteLiabilityForm(formPath);
        } catch {
          // Non-fatal: the new form is already saved.
        }
      }
      setFormPath(newPath);
      set('liability_status', 'signed');
      toast.success('Signed form attached.');
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  async function viewForm() {
    if (!formPath) return;
    const w = window.open('', '_blank');
    try {
      const url = await liabilityFormUrl(formPath);
      if (w) w.location.href = url;
    } catch (e) {
      w?.close();
      toast.error(errorMessage(e));
    }
  }

  async function removeForm() {
    if (!formPath || !client) return;
    const ok = await confirm({
      title: 'Remove the attached form?',
      body: 'The stored file is deleted. The liability status stays as it is — change it below if needed.',
      confirmLabel: 'Remove file',
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteLiabilityForm(formPath);
      const { error } = await supabase
        .from('clients')
        .update({ liability_form_path: null })
        .eq('id', client.id);
      if (error) throw error;
      setFormPath(null);
      toast.success('Form removed.');
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }

  return (
    <Modal title={client ? 'Edit client' : 'Add client'} onClose={onClose}>
      <form onSubmit={submit}>
        <div className="field-row">
          <div className="field">
            <label htmlFor="c-first">First name</label>
            <input
              id="c-first"
              value={f.first_name}
              onChange={(e) => set('first_name', e.target.value)}
              required
              autoFocus
            />
          </div>
          <div className="field">
            <label htmlFor="c-last">Last name</label>
            <input
              id="c-last"
              value={f.last_name}
              onChange={(e) => set('last_name', e.target.value)}
              required
            />
          </div>
        </div>
        <div className="field-row">
          <div className="field">
            <label htmlFor="c-phone">Phone</label>
            <input id="c-phone" value={f.phone} onChange={(e) => set('phone', e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="c-email">Email</label>
            <input
              id="c-email"
              type="email"
              value={f.email}
              onChange={(e) => set('email', e.target.value)}
            />
          </div>
        </div>
        <div className="field-row">
          <div className="field">
            <label htmlFor="c-dog">Dog name</label>
            <input
              id="c-dog"
              value={f.dog_name}
              onChange={(e) => set('dog_name', e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="c-breed">Breed</label>
            <input id="c-breed" value={f.breed} onChange={(e) => set('breed', e.target.value)} />
          </div>
        </div>
        <div className="field-row">
          <div className="field">
            <label htmlFor="c-age">Dog age (years)</label>
            <input
              id="c-age"
              type="number"
              min={0}
              max={40}
              value={f.age}
              onChange={(e) => set('age', e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="c-liab">Liability form</label>
            <select
              id="c-liab"
              value={f.liability_status}
              onChange={(e) => set('liability_status', e.target.value)}
            >
              <option value="none">Not sent</option>
              <option value="sent">Sent — awaiting signature</option>
              <option value="signed">Signed</option>
            </select>
          </div>
        </div>

        <div className="field">
          <label>Signed form file</label>
          {client ? (
            <>
              <input
                ref={fileInput}
                type="file"
                accept=".pdf,application/pdf,image/*"
                style={{ display: 'none' }}
                onChange={(e) => void onFilePicked(e.target.files?.[0] ?? null)}
              />
              <div className="row wrap">
                {formPath ? (
                  <>
                    <span className="badge badge-success">📎 On file</span>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => void viewForm()}
                    >
                      View
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => fileInput.current?.click()}
                      disabled={uploading}
                    >
                      {uploading ? 'Uploading…' : 'Replace…'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      style={{ color: 'var(--danger)' }}
                      onClick={() => void removeForm()}
                    >
                      Remove
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => fileInput.current?.click()}
                    disabled={uploading}
                  >
                    {uploading ? 'Uploading…' : '📎 Attach signed PDF…'}
                  </button>
                )}
              </div>
              <p className="muted small" style={{ margin: '0.35rem 0 0' }}>
                Stored privately — only you can open it. Attaching a file marks the status "Signed".
              </p>
            </>
          ) : (
            <p className="muted small" style={{ margin: 0 }}>
              Save the client first, then reopen to attach the signed PDF.
            </p>
          )}
        </div>

        <div className="field">
          <label htmlFor="c-notes">Notes</label>
          <textarea
            id="c-notes"
            value={f.notes}
            onChange={(e) => set('notes', e.target.value)}
            placeholder="Anything to remember about this client or dog…"
          />
        </div>
        {err && <p className="error-text">{err}</p>}
        <div className="row" style={{ justifyContent: 'flex-end', marginTop: '0.6rem' }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={busy || !f.first_name.trim() || !f.last_name.trim()}
          >
            {busy ? 'Saving…' : 'Save client'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
